import {
    Alert,
    Dialog,
    Heading,
    Pane,
    Spinner,
    Strong,
    Text,
} from 'evergreen-ui';
import { Component } from 'react';
import Request from '../../../util/Api';
import Config from '../Config';

const POLL_INTERVAL_MS = 2000;

const STATUS_LABELS = {
    idle: 'Inactiv',
    fetching: 'Se preiau cartile',
    syncing: 'Sincronizare',
    done: 'Finalizat',
    error: 'Eroare',
};

const STATUS_COLORS = {
    idle: '#8f95b2',
    fetching: '#1070ca',
    syncing: '#1070ca',
    done: '#52bd95',
    error: '#d14343',
};

class ProgressBar extends Component {
    render() {
        const { value = 0, max = 0, color = '#1070ca' } = this.props;
        const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
        return (
            <Pane background="tint2" height={8} borderRadius={4} overflow="hidden" width="100%">
                <Pane background={color} height="100%" width={`${pct}%`} transition="width 0.3s ease" />
            </Pane>
        );
    }
}

class CardSyncProgressModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            progress: null,
            error: null,
        };
        this._pollTimer = null;
        this._mounted = false;
    }

    componentDidMount() {
        this._mounted = true;
        if (this.props.isShown) {
            this.fetchStatus();
            this.startPolling();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.isShown && !prevProps.isShown) {
            this.fetchStatus();
            this.startPolling();
        } else if (!this.props.isShown && prevProps.isShown) {
            this.stopPolling();
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        this.stopPolling();
    }

    startPolling = () => {
        this.stopPolling();
        this._pollTimer = setInterval(this.fetchStatus, POLL_INTERVAL_MS);
    };

    stopPolling = () => {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    };

    fetchStatus = async () => {
        try {
            const res = await Request.get(Config.Endpoints.resyncExpansionStatus);
            if (!this._mounted) return;
            const progress = res && res.progress ? res.progress : null;
            this.setState({ progress, error: null });

            if (progress && !progress.running) {
                this.stopPolling();
                if (progress.status === 'done' && this.props.onComplete) {
                    this.props.onComplete();
                }
            }
        } catch (err) {
            if (!this._mounted) return;
            this.setState({ error: 'Nu s-a putut obtine progresul sincronizarii' });
        }
    };

    handleClose = () => {
        this.stopPolling();
        this.props.onClose && this.props.onClose();
    };

    renderBody() {
        const { progress, error } = this.state;

        if (error) {
            return <Alert intent="danger" title="Eroare">{error}</Alert>;
        }

        if (!progress) {
            return (
                <Pane display="flex" alignItems="center" justifyContent="center" padding={20}>
                    <Spinner size={24} />
                    <Text marginLeft={12}>Se incarca progresul...</Text>
                </Pane>
            );
        }

        const status = progress.status || 'idle';
        const color = STATUS_COLORS[status] || '#1070ca';
        const value = progress.processedCards || 0;
        const max = progress.totalCards || 0;

        return (
            <Pane>
                <Pane marginBottom={16}>
                    <Pane display="flex" justifyContent="space-between" marginBottom={4}>
                        <Heading size={400}>
                            {progress.expansionName
                                ? `Extensie: ${progress.expansionName}`
                                : 'Resincronizare carti'}
                        </Heading>
                        <Text color={color}>{STATUS_LABELS[status] || status}</Text>
                    </Pane>
                    <ProgressBar value={value} max={max} color={color} />
                    <Pane display="flex" justifyContent="space-between" marginTop={4}>
                        <Text size={300} color="muted">
                            {status === 'fetching'
                                ? 'Se preiau cartile de la furnizor...'
                                : `Procesat ${value}/${max} carti`}
                        </Text>
                        {progress.startedAt && (
                            <Text size={300} color="muted">
                                Pornit: {new Date(progress.startedAt).toLocaleTimeString()}
                            </Text>
                        )}
                    </Pane>
                </Pane>

                {progress.error && (
                    <Alert intent="danger" title="Eroare" marginBottom={12}>
                        {progress.error}
                    </Alert>
                )}

                {!progress.running && progress.status === 'done' && (
                    <Alert intent="success" title="Sincronizarea s-a incheiat cu succes" />
                )}

                {!progress.running && progress.status !== 'done' && progress.status !== 'error' && (
                    <Pane>
                        <Strong>Nu este pornita nicio sincronizare.</Strong>
                    </Pane>
                )}
            </Pane>
        );
    }

    render() {
        const { progress } = this.state;
        const isRunning = progress && progress.running;

        return (
            <Dialog
                isShown={this.props.isShown}
                title="Resincronizare carti"
                onCloseComplete={this.handleClose}
                hasFooter={true}
                hasCancel={false}
                confirmLabel={isRunning ? 'Ruleaza in fundal' : 'Inchide'}
                onConfirm={this.handleClose}
                shouldCloseOnEscapePress={!isRunning}
                shouldCloseOnOverlayClick={!isRunning}
                width={640}
            >
                {this.renderBody()}
            </Dialog>
        );
    }
}

export default CardSyncProgressModal;
